import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const DeleteConfirmationModal = ({ shop, onConfirm, onCancel }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!shop) return null;

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-black bg-opacity-50' : 'bg-gray-900 bg-opacity-30'} flex items-center justify-center p-4 z-50`}>
      <div className="relative group/modal">
        {/* Modal Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 backdrop-blur-3xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'bg-white border border-slate-200 shadow-lg'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        {/* Glass Particles (nur Dark) */}
        <div className={`absolute top-4 right-6 w-2 h-2 ${isDarkMode ? 'bg-white/20' : 'hidden'} rounded-full animate-pulse`}></div>

        <div className="relative p-6 w-full max-w-md">
          <div className="flex items-center space-x-3 mb-4">
            {/* Alert Icon */}
            <div className="relative group/alert">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent'
                : 'hidden'
              } rounded-2xl blur-sm`}></div>
              <div className={`relative ${isDarkMode
                ? 'bg-gradient-to-br from-red-600/60 via-red-700/50 to-red-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(239,68,68,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                : 'bg-red-50 border border-red-200'
              } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                <AlertCircle className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-red-600'}`} />
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>
            <div>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('admin.shopDelete.title')}</h3>
              <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('admin.shopDelete.subtitle')}</p>
            </div>
          </div>

          <p className={`${isDarkMode ? 'text-white/80' : 'text-slate-700'} mb-6`}>
            {t('admin.shopDelete.confirm', { name: shop.name })}
          </p>

          <div className="flex space-x-3">
            {/* Cancel Button */}
            <button
              onClick={onCancel}
              className={`relative group/cancel flex-1 overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'
              }`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              {/* Hover Overlay (nur Dark) */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)' }}
              ></div>

              <div className={`relative z-10 px-4 py-2 font-medium transition-all duration-300 ${
                isDarkMode ? 'text-white' : 'text-slate-700'
              }`}>
                {t('common.cancel')}
              </div>

              {/* Wave Effect (nur Dark) */}
              <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)' }}
                ></div>
              </div>
            </button>

            {/* Delete Button */}
            <button
              onClick={() => onConfirm(shop.id)}
              className="relative group/delete flex-1 overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99]"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(239,68,68,0.7) 0%, rgba(220,38,38,0.7) 50%, rgba(185,28,28,0.7) 100%)'
                  : '#DC2626',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : 'none',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : 'none'
              }}
            >
              {/* Hover Overlay */}
              <div
                className="absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/delete:opacity-100"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    : 'rgba(255,255,255,0.12)'
                }}
              ></div>

              <div className="relative z-10 px-4 py-2 font-medium text-white transition-all duration-300">
                {t('common.delete')}
              </div>

              {/* Wave Effect (nur Dark) */}
              <div className={`absolute inset-0 opacity-0 group-hover/delete:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/delete:translate-x-full transition-transform duration-800"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
